/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tag, Tooltip, Button, Col, Input, Select } from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import { FileSearchOutlined, WarningOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { Filters, FiltersCard } from "../../components/Filters";
import { Label } from "../../components/ui";

import api from "../../services/api";

import { useToast } from "../../hooks/toast";
import { useAuth } from "../../hooks/auth";
// eslint-disable-next-line import/extensions
import Table from "../../components/Table";
import UserAvatar from "../../components/UserAvatar";
import { Container } from "./styles";
import { ICustomer } from "../Customers";
import { IUser } from "../Users";
import ProjectTypes from "../../enums/ProjectTypes";
import ProjectStatus from "../../enums/ProjectStatus";
import UserRoles from "../../enums/UserRoles";
import { IRequirement } from "../Project";
import isWarningActivity from "../../utils/isWarningActivity";

const { Search } = Input;

export interface IProject {
  id: string;
  identifier: string;
  short_description: string;
  full_description: string;
  contractor_name: string;
  contractor_email: string;
  contractor_phone_number: string;
  status: string;
  purchase_order: string;
  customer: ICustomer;
  manager: IUser;
  projectType: IProjectType;
  project_type_id: string;
  expected_start: string;
  expected_end: string;
  requirements: IRequirement[];
}

export interface IProjectType {
  id: string;
  name: string;
  acronym: ProjectTypes;
}

const ProjectInternal: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);
  const [managers, setManagers] = useState<IUser[]>([]);
  const [projectTypes, setProjectTypes] = useState<IProjectType[]>([]);

  const { addToast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation(["projects"]);

  // Filters
  const [identifier, setIdentifier] = useState<string | undefined>();
  const [identifierValue, setIdentifierValue] = useState<string | undefined>(
    "",
  );
  const [status, setStatus] = useState();
  const [managerFilter, setManagerFilter] = useState();
  const [projectTypeFilter, setProjectTypeFilter] = useState();
  const [shortDescriptionValue, setShortDescriptionValue] = useState<
    string | undefined
  >("");
  const [shortDescription, setShortDescription] = useState<
    string | undefined
  >();

  const [receiptValue, setReceiptValue] = useState<string | undefined>("");

  const searchParams = useMemo(() => {
    return {
      identifier,
      status,
      manager_id: managerFilter,
      project_type_id: projectTypeFilter,
      short_description: shortDescription,
    };
  }, [identifier, status, managerFilter, projectTypeFilter, shortDescription]);

  useEffect(() => {
    setLoading(true);
    api
      .get<IProject[]>("/projects/internal", { params: searchParams })
      .then(response => {
        setProjects(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os projetos, tente novamente.",
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    setLoadingManagers(true);
    api
      .get<IUser[]>("/users", {
        params: {
          role: "MANAGER",
        },
      })
      .then(response => {
        setManagers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingManagers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoadingProjectTypes(true);
    api
      .get<IProjectType[]>("/project-types")
      .then(response => {
        setProjectTypes(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingProjectTypes(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const managerOptions = useMemo(() => {
    if (!managers) {
      return undefined;
    }

    return managers.map(manager => ({
      label: manager.name,
      value: manager.id,
    }));
  }, [managers]);

  const projectTypesOptions = useMemo(() => {
    if (!projectTypes) {
      return undefined;
    }

    return projectTypes.map(projectType => ({
      label: t(`project-types.options.${projectType.acronym}.label`),
      value: projectType.id,
    }));
  }, [projectTypes, t]);

  const statusOptions = useMemo(() => {
    return (Object.keys(ProjectStatus) as (keyof typeof ProjectStatus)[]).map(
      (key, _) => {
        return {
          label: t(`status.options.${ProjectStatus[key]}.label`),
          value: ProjectStatus[key],
        };
      },
    );
  }, [t]);

  const columns: ColumnsType<IProject> = [
    {
      title: t("identifier"),
      dataIndex: "identifier",
    },
    {
      title: t("short_description"),
      dataIndex: "short_description",
    },
    {
      title: t("customer"),
      dataIndex: "customer",
      render: (customer: ICustomer | undefined) => {
        return customer?.name;
      },
    },
    {
      title: t("project-types.label"),
      dataIndex: "projectType",
      render: (projectType: IProjectType | undefined) => {
        return (
          <Tag
            color={t(`project-types.options.${projectType?.acronym}.color`)}
            key={projectType?.acronym}
          >
            {t(`project-types.options.${projectType?.acronym}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("status.label"),
      dataIndex: "status",
      render: (status: string) => {
        return (
          <Tag color={t(`status.options.${status}.color`)} key={status}>
            {t(`status.options.${status}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("manager"),
      dataIndex: "manager",
      render: (manager: IUser) => {
        if (manager) {
          return (
            <UserAvatar
              name={manager.name}
              url={manager.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
    },
    {
      title: t("actions.label"),
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Link to={`/app/projects/${record.id}`}>
            <FileSearchOutlined />
          </Link>
          {isWarningActivity(record.requirements) ? (
            <Tooltip title="Projeto possue atividade(s) com impedimento(s)">
              <Button type="link" icon={<WarningOutlined />} />
            </Tooltip>
          ) : (
            <></>
          )}
        </>
      ),
    },
  ];

  const handleShowFilters = useCallback(
    value => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showFilters) {
        setStatus(undefined);
        setIdentifier(undefined);
        setManagerFilter(undefined);
        setProjectTypeFilter(undefined);
        setShortDescription(undefined);
        setReceiptValue(undefined);
      }
    },
    [showFilters],
  );

  const extraFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("status.label")}</Label>
            </div>
            <Select
              placeholder={t("status.label")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              options={statusOptions}
              onChange={value => setStatus(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("identifier")}</Label>
            </div>
            <Search
              placeholder={t("identifier")}
              onSearch={value => {
                setIdentifier(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setIdentifierValue(value || undefined);
              }}
              value={identifierValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("project-types.label")}</Label>
            </div>
            <Select
              allowClear
              options={projectTypesOptions}
              placeholder={t("project-types.label")}
              loading={loadingProjectTypes}
              style={{ width: "100%" }}
              onChange={value => setProjectTypeFilter(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("manager")}</Label>
            </div>
            <Select
              allowClear
              options={managerOptions}
              placeholder={t("manager")}
              loading={loadingManagers}
              style={{ width: "100%" }}
              onChange={value => setManagerFilter(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("Descrição Resumida")}</Label>
            </div>
            <Search
              placeholder={t("Descrição Resumida")}
              onSearch={value => {
                setShortDescription(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setShortDescriptionValue(value || undefined);
              }}
              value={shortDescriptionValue}
              allowClear
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [
      identifierValue,
      shortDescriptionValue,
      receiptValue,
      loadingManagers,
      loadingProjectTypes,
      managerOptions,
      projectTypesOptions,
      showFilters,
      statusOptions,
      t,
    ],
  );

  return (
    <Container>
      <Table
        data={projects}
        columns={columns}
        title={
          <>
            Projetos
            <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            />
          </>
        }
        footer={() => <></>}
        loading={loading}
        pagination={false}
        extraFilters={extraFilters}
      />
    </Container>
  );
};

export default ProjectInternal;
